// hooks/useOptions.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../services/axiosInterceptor';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Raw per-option result returned by the PHP backend */
interface OptionResult<T = unknown> {
  status: 'success' | 'error';
  data?: T;
  message?: string;
}

/** The top-level API envelope from /multiple-options */
interface MultipleOptionsApiResponse<T = unknown> {
  status: 'success' | 'error';
  data: Record<string, OptionResult<T>>;
  message?: string;
}

/** State shape for a single named option */
export interface OptionState<T = unknown> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

/** The value returned by the hook */
export interface UseOptionsReturn<T = unknown> {
  /** One entry per option name you requested */
  options: Record<string, OptionState<T>>;
  /** True while ANY option is still loading */
  loading: boolean;
  /** First error encountered across all options (null if none) */
  error: string | null;
  /** Manually re-fetch all options */
  refetch: () => void;
}

// ─── Config ───────────────────────────────────────────────────────────────────

export interface UseOptionsConfig {
  /**
   * Whether to fetch immediately on mount.
   * Set to false if you want to call `refetch()` manually.
   * @default true
   */
  enabled?: boolean;
  /**
   * Re-fetch whenever this value changes (like a dependency in useEffect).
   * Useful when your option names depend on other state.
   */
  deps?: unknown[];
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useOptions — fetch one or many WordPress-style options from /multiple-options.
 *
 * @example Single option
 * ```ts
 * const { options } = useOptions<string[]>('school_zone');
 * const zone = options['school_zone'].data; // string[] | null
 * ```
 *
 * @example Multiple options
 * ```ts
 * const { options, loading } = useOptions<unknown>(['school_zone', 'current_session']);
 * const zone    = options['school_zone'].data;
 * const session = options['current_session'].data;
 * ```
 *
 * @example Typed per-option (cast after)
 * ```ts
 * const { options } = useOptions(['school_zone', 'current_session']);
 * const zone = options['school_zone'].data as string[];
 * ```
 */
export function useOptions<T = unknown>(
  names: string | string[],
  config: UseOptionsConfig = {}
): UseOptionsReturn<T> {
  const { enabled = true, deps = [] } = config;

  // Normalise to a stable comma-joined key so we can use it as an effect dep
  const namesArray = Array.isArray(names) ? names : [names];
  const namesKey = namesArray.join(',');

  // Build the initial loading state (one entry per name)
  const buildInitialState = useCallback(
    (): Record<string, OptionState<T>> =>
      namesArray.reduce(
        (acc, name) => ({
          ...acc,
          [name]: { data: null, loading: true, error: null },
        }),
        {} as Record<string, OptionState<T>>
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [namesKey]
  );

  const [options, setOptions] = useState<Record<string, OptionState<T>>>(
    buildInitialState
  );

  // A counter we increment to trigger a re-fetch from outside
  const [fetchIndex, setFetchIndex] = useState(0);
  const refetch = useCallback(() => setFetchIndex((n) => n + 1), []);

  // Track whether the component is still mounted so we don't set state after unmount
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // Reset to loading whenever names / fetch trigger changes
    setOptions(buildInitialState());

    let cancelled = false;

    const fetchOptions = async () => {
      try {
        const response = await apiClient.get<MultipleOptionsApiResponse<T>>(
          'multiple-options',
          { params: { names: namesKey } }
        );

        if (cancelled || !mountedRef.current) return;

        if (response.data.status === 'success') {
          const incoming = response.data.data;

          // Merge each option result into state
          setOptions(
            namesArray.reduce((acc, name) => {
              const result = incoming[name];

              if (!result) {
                acc[name] = {
                  data: null,
                  loading: false,
                  error: `Option "${name}" was not returned by the server.`,
                };
              } else if (result.status === 'success') {
                acc[name] = {
                  data: (result.data as T) ?? null,
                  loading: false,
                  error: null,
                };
              } else {
                acc[name] = {
                  data: null,
                  loading: false,
                  error: result.message ?? `Failed to load option "${name}".`,
                };
              }

              return acc;
            }, {} as Record<string, OptionState<T>>)
          );
        } else {
          // Top-level API error — mark all options as failed
          const apiError = response.data.message ?? 'Failed to load options.';
          setOptions(
            namesArray.reduce(
              (acc, name) => ({
                ...acc,
                [name]: { data: null, loading: false, error: apiError },
              }),
              {} as Record<string, OptionState<T>>
            )
          );
        }
      } catch (err: unknown) {
        if (cancelled || !mountedRef.current) return;

        const message =
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ??
          (err as { message?: string })?.message ??
          'An unexpected error occurred while fetching options.';

        setOptions(
          namesArray.reduce(
            (acc, name) => ({
              ...acc,
              [name]: { data: null, loading: false, error: message },
            }),
            {} as Record<string, OptionState<T>>
          )
        );
      }
    };

    fetchOptions();

    return () => {
      cancelled = true;
    };
  }, [namesKey, enabled, fetchIndex, buildInitialState, ...deps]);

  // Derive aggregate loading / error
  const values = Object.values(options);
  const loading = values.some((o) => o.loading);
  const error = values.find((o) => o.error)?.error ?? null;

  return { options, loading, error, refetch };
}
